import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { FaTimes, FaUserEdit, FaTrash, FaKey, FaShieldAlt, FaEye } from 'react-icons/fa';
import { showSuccess, showError, showConfirm, showLoading, closeLoading } from '../../../utils/Alert';
import './UserModals.css';

const EditUserModal = ({ isOpen, onClose, user, onUserUpdated, isViewOnly = false }) => {
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => { document.body.style.overflow = 'unset'; };
    }, [isOpen]);

    const [formData, setFormData] = useState({
        name: '',
        role: 'student',
        status: 'active',
        bio: '',
        phone: '',
        location: ''
    });

    useEffect(() => {
        if (user) {
            setFormData({
                name: user.name || '',
                role: user.role || 'student',
                status: user.status || 'active',
                bio: user.bio || '',
                phone: user.phone || '',
                location: user.location || ''
            });
        }
    }, [user]);

    const rolesList = [
        { value: 'student', label: 'Academic Student' },
        { value: 'tutor', label: 'Platform Tutor' },
        { value: 'recruiter', label: 'Agency Recruiter' },
        { value: 'job_seeker', label: 'Active Job Seeker' }
    ];

    const handleSubmit = async (e) => {
        e.preventDefault();
        showLoading('Updating Entity...');
        try {
            await axios.put(`/api/admin/users/${user._id}`, formData, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            closeLoading();
            showSuccess('Success', 'User profile updated successfully');
            onUserUpdated();
            onClose();
        } catch (err) {
            closeLoading();
            showError('Update Failed', err.response?.data?.message || 'Error occurred');
        }
    };

    const handleDelete = async () => {
        const confirmed = await showConfirm(
            'Terminate Entity?',
            `Permanently delete ${user.email}? This action is irreversible.`,
            'error'
        );
        if (confirmed) {
            showLoading('Purging Data...');
            try {
                await axios.delete(`/api/admin/users/${user._id}`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                });
                closeLoading();
                showSuccess('Purge Complete', 'Entity removed from platform');
                onUserUpdated();
                onClose();
            } catch (err) {
                closeLoading();
                showError('Purge Failed', err.response?.data?.message || 'Error occurred');
            }
        }
    };

    if (!user) return null;

    return ReactDOM.createPortal(
        <div className={`modal-overlay ${isOpen ? 'open' : ''} ${isViewOnly ? 'view-mode' : ''}`} onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>
                        {isViewOnly ? <FaEye /> : <FaUserEdit />}
                        {isViewOnly ? 'Identity Intelligence' : 'Modify Identity Parameters'}
                    </h2>
                    <button className="modal-close-btn" onClick={onClose}><FaTimes /></button>
                </div>

                <div className="modal-body">
                    <div className="user-identifier-badge">
                        <div className="identifier-block">
                            <span className="identifier-label">Reference Email</span>
                            <div className="identifier-hash">{user.email}</div>
                        </div>
                        <div className="identifier-block" style={{ textAlign: 'right' }}>
                            <span className="identifier-label">Entity Status</span>
                            <div style={{ color: user.status === 'active' ? '#10b981' : '#ef4444', fontWeight: '800' }}>
                                {user.status.toUpperCase()}
                            </div>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="modal-form">
                        <div className="form-group">
                            <label className="premium-label">Legal Name</label>
                            <input
                                type="text"
                                className="glass-input"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                readOnly={isViewOnly}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label className="premium-label">Assigned Role</label>
                            <select
                                className="glass-input"
                                value={formData.role}
                                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                                disabled={isViewOnly || user.role === 'super_admin'}
                            >
                                {rolesList.map(r => (
                                    <option key={r.value} value={r.value}>{r.label}</option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group full-width">
                            <label className="premium-label">Bio / Description</label>
                            <textarea
                                className="glass-input resize-none"
                                value={formData.bio}
                                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                                readOnly={isViewOnly}
                                placeholder="Tell us about the entity's role..."
                                rows="3"
                            ></textarea>
                        </div>

                        <div className="form-group">
                            <label className="premium-label">Contact Number</label>
                            <input
                                type="tel"
                                className="glass-input"
                                value={formData.phone}
                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                readOnly={isViewOnly}
                                placeholder="+1 (555) 000-0000"
                            />
                        </div>

                        <div className="form-group">
                            <label className="premium-label">Geographic Location</label>
                            <input
                                type="text"
                                className="glass-input"
                                value={formData.location}
                                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                                readOnly={isViewOnly}
                                placeholder="e.g. San Francisco, CA"
                            />
                        </div>

                        {!isViewOnly && user.role !== 'super_admin' && (
                            <div className="form-group full-width">
                                <label className="premium-label">Operational Status</label>
                                <div className="status-toggle-wrapper">
                                    <div
                                        className={`status-option ${formData.status === 'active' ? 'active-selection' : ''}`}
                                        onClick={() => setFormData({ ...formData, status: 'active' })}
                                    >
                                        <FaShieldAlt /> Authorized (Active)
                                    </div>
                                    <div
                                        className={`status-option ${formData.status === 'inactive' ? 'inactive-selection' : ''}`}
                                        onClick={() => setFormData({ ...formData, status: 'inactive' })}
                                    >
                                        <FaTimes /> Suspended (Inactive)
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="modal-actions">
                            {!isViewOnly && user.role !== 'super_admin' && (
                                <button type="button" className="action-btn-danger outlined" onClick={handleDelete} style={{ marginRight: 'auto' }}>
                                    <FaTrash /> Purge Entity
                                </button>
                            )}

                            {!isViewOnly && (
                                <>
                                    <button type="submit" className="action-btn-gold">
                                        Update Intelligence
                                    </button>
                                </>
                            )}

                            {isViewOnly && (
                                <button type="button" className="action-btn-gold" onClick={onClose}>
                                    Acknowledge
                                </button>
                            )}
                        </div>
                    </form>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default EditUserModal;
